import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { PlaceExperience } from "@/components/place/types";
import { formatMoney } from "@/lib/formatMoney";
import { experienceHref } from "@/lib/experiences";

interface EventRow {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  event_type: string;
  price_ore: number;
  is_free: boolean;
  status: string;
  producer_id: string;
  booking_mode: string | null;
  booking_deadline_hours: number | null;
  max_spots: number | null;
  booked_spots: number | null;
  use_producer_address: boolean | null;
  custom_address: string | null;
  producers: {
    name: string;
    slug: string;
  } | null;
}

/**
 * Fetches upcoming public events from producers linked to a place.
 * Uses public RPC with booking/capacity aggregates.
 * Images come from media_assets, not entity fields.
 */
export function usePlaceExperiences(producerIds: string[], limit = 6) {
  const query = useQuery({
    queryKey: ["place-experiences", producerIds, limit],
    queryFn: async (): Promise<PlaceExperience[]> => {
      if (!producerIds.length) return [];

      const now = new Date().toISOString();

      const { data, error } = await supabase
        .rpc("get_public_events_with_bookings" as any, {
          p_producer_ids: producerIds,
          p_limit: Math.max(limit * 3, 20),
        });

      if (error) {
        console.error("Error fetching place experiences:", error);
        throw error;
      }

      const rows = ((data as unknown as EventRow[] | null) || [])
        .filter((row) => row.start_date >= now)
        .slice(0, limit);

      const uniqueProducerIds = [...new Set(rows.map((row) => row.producer_id).filter(Boolean))];
      const { data: producers } = await supabase
        .from('producers')
        .select('id, name, slug')
        .in('id', uniqueProducerIds)
        .eq('status', 'public');

      const producerMap = new Map((producers || []).map((producer: any) => [producer.id, producer]));

      return rows.map((row) => {
        const producer = producerMap.get(row.producer_id) || null;
        let priceText: string | undefined;
        if (row.is_free) {
          priceText = "Gratis";
        } else if (row.price_ore > 0) {
          priceText = `från ${formatMoney(row.price_ore)}`;
        }

        const bookingUrl = experienceHref({ type: row.event_type, id: row.id });

        // Build venue text for custom address
        let venueText: string | null = null;
        if (!row.use_producer_address && row.custom_address) {
          venueText = row.custom_address;
        }

        return {
          id: row.id,
          title: row.title,
          startsAt: row.start_date,
          endsAt: row.end_date,
          priceText,
          imageUrl: undefined, // Media from media_assets pipeline
          producerName: producer?.name ?? undefined,
          bookingUrl,
          eventType: row.event_type,
          bookingMode: row.booking_mode ?? "none",
          bookingDeadlineHours: row.booking_deadline_hours,
          maxSpots: row.max_spots,
          bookedSpots: row.booked_spots ?? 0,
          useProducerAddress: row.use_producer_address ?? true,
          venueText,
        };
      });
    },
    enabled: producerIds.length > 0,
  });

  return {
    events: query.data ?? [],
    loading: query.isLoading,
    error: query.error?.message ?? null,
  };
}
